import { useEffect, useState } from 'react';
import {
  createPromoCode,
  deletePromoCode,
  getPromoCodes,
  updatePromoCode,
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Tag, Trash2 } from 'lucide-react';

const emptyForm = {
  code: '',
  type: 'fixed_mxn',
  value: 30,
  min_order_mxn: 0,
  max_uses: 0,
  active: true,
  single_use_per_phone: true,
};

export default function AdminPromoCodes() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getPromoCodes()
      .then(setList)
      .catch(() => toast.error('No se pudieron cargar los cupones'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      toast.error('Escribe un código');
      return;
    }
    setSaving(true);
    try {
      await createPromoCode(form);
      toast.success('Cupón creado');
      setForm(emptyForm);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'No se pudo crear el cupón');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (row) => {
    try {
      await updatePromoCode(row.id, { active: !row.active });
      load();
    } catch {
      toast.error('Error al actualizar');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este cupón?')) return;
    try {
      await deletePromoCode(id);
      toast.success('Cupón eliminado');
      load();
    } catch {
      toast.error('No se pudo eliminar');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Tag className="w-6 h-6 text-primary" />
          Cupones de descuento
        </h2>
        <p className="text-muted-foreground text-sm mt-1">
          Los cupones de la app de Mercado Pago son aparte (panel MP). Aquí defines códigos propios de Pizzas
          para web y WhatsApp.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Nuevo cupón</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Código</Label>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="PIZZA30"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Tipo</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 h-10"
                  value={form.type}
                  onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                >
                  <option value="fixed_mxn">Monto fijo (MXN)</option>
                  <option value="percent">Porcentaje (%)</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Valor</Label>
                <Input
                  type="number"
                  min={1}
                  value={form.value}
                  onChange={(e) => setForm((f) => ({ ...f, value: parseFloat(e.target.value) || 0 }))}
                />
              </div>
              <div>
                <Label>Pedido mínimo (MXN)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.min_order_mxn}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, min_order_mxn: parseFloat(e.target.value) || 0 }))
                  }
                />
              </div>
              <div>
                <Label>Máx. usos (0 = ilimitado)</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.max_uses}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, max_uses: parseInt(e.target.value, 10) || 0 }))
                  }
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={form.single_use_per_phone}
                onCheckedChange={(v) => setForm((f) => ({ ...f, single_use_per_phone: Boolean(v) }))}
              />
              Un solo uso por teléfono
            </label>
            <Button type="submit" disabled={saving}>
              {saving ? 'Guardando…' : 'Crear cupón'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Cupones activos</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-muted-foreground text-sm">Cargando…</p>
          ) : !list.length ? (
            <p className="text-muted-foreground text-sm">No hay cupones. Crea uno arriba.</p>
          ) : (
            <ul className="space-y-2">
              {list.map((row) => (
                <li
                  key={row.id}
                  className="flex flex-wrap items-center justify-between gap-2 border rounded-lg p-3 text-sm"
                >
                  <div>
                    <span className="font-mono font-bold">{row.code}</span>
                    <span className="text-muted-foreground ml-2">
                      {row.type === 'percent' ? `${row.value}%` : `$${row.value} MXN`}
                      {row.min_order_mxn > 0 ? ` · mín. $${row.min_order_mxn}` : ''}
                    </span>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Usos: {row.used_count}
                      {row.max_uses > 0 ? ` / ${row.max_uses}` : ''}
                      {!row.active ? ' · INACTIVO' : ''}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={() => toggleActive(row)}>
                      {row.active ? 'Desactivar' : 'Activar'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => remove(row.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
